import { 
  collection, 
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query, 
  where,
  limit,
  onSnapshot,
  getDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getWaitlistByBarbershop, markAsNotified } from './waitlistService';
import { sendWaitlistNotificationEmail } from './backendService';
import { getUserById } from './userService';
import { createAuditLog } from './auditService';
import { createNotification } from './notificationService';
import { auth } from '../config/firebase';

const COLLECTION = 'availableSlots';

/**
 * Criar novo horário disponível
 */
export const createSlot = async (slotData) => {
  try {
    // Validar se o horário não está no passado (usando horário local)
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${day}`;
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

    if (slotData.date < currentDate) {
      return { success: false, error: 'Não é possível criar horários em datas passadas' };
    }
    if (slotData.date === currentDate && slotData.time <= currentTime) {
      return { success: false, error: 'Não é possível criar horários que já passaram hoje' };
    }

    // Verificar se já existe um slot no mesmo horário, data e barbearia
    const existingSlots = await getSlotsByBarbershopAndDate(slotData.barbershopId, slotData.date);
    
    if (existingSlots.success && existingSlots.data.length > 0) {
      const duplicate = existingSlots.data.find(slot => slot.time === slotData.time);
      if (duplicate) {
        const formattedDate = new Date(slotData.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        return { 
          success: false, 
          error: `Já existe um horário cadastrado para ${slotData.time} no dia ${formattedDate}` 
        };
      }
    }

    const docRef = await addDoc(collection(db, COLLECTION), {
      ...slotData,
      isBooked: false,
      createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating slot:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar slots de uma barbearia em uma data específica
 */
export const getSlotsByBarbershopAndDate = async (barbershopId, date) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('barbershopId', '==', barbershopId),
      where('date', '==', date),
      limit(100)
    );
    const querySnapshot = await getDocs(q);
    const slots = [];
    
    querySnapshot.forEach((doc) => {
      slots.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: slots };
  } catch (error) {
    console.error('Error getting slots:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar todos os slots de uma barbearia
 */
export const getSlotsByBarbershop = async (barbershopId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('barbershopId', '==', barbershopId),
      limit(100)
    );
    const querySnapshot = await getDocs(q);
    const slots = [];
    
    querySnapshot.forEach((doc) => {
      slots.push({ id: doc.id, ...doc.data() });
    });
    
    // Ordenar manualmente por data e time
    slots.sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      return a.time.localeCompare(b.time);
    });
    
    return { success: true, data: slots };
  } catch (error) {
    console.error('Error getting barbershop slots:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar slot
 */
export const deleteSlot = async (slotId) => {
  try {
    const docRef = doc(db, COLLECTION, slotId);
    
    // Buscar dados do slot antes de deletar para o log
    const slotSnap = await getDoc(docRef);
    const slotData = slotSnap.exists() ? slotSnap.data() : null;
    
    await deleteDoc(docRef);
    
    // Registrar log de auditoria
    const currentUser = auth.currentUser;
    if (currentUser && slotData) {
      await createAuditLog({
        action: 'slot_deleted',
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userRole: 'owner', // Apenas owners podem deletar slots
        barbershopId: slotData.barbershopId,
        details: {
          slotId: slotId,
          date: slotData.date,
          time: slotData.time
        }
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting slot:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Marcar múltiplos slots como ocupados (para serviços longos)
 * @param {Array} slotIds - Array de IDs dos slots a marcar como ocupados
 * @param {string} appointmentId - ID do agendamento
 */
export const markMultipleSlotsAsBooked = async (slotIds, appointmentId) => {
  try {
    const promises = slotIds.map(slotId => 
      updateDoc(doc(db, COLLECTION, slotId), {
        isBooked: true,
        appointmentId: appointmentId,
        updatedAt: new Date().toISOString()
      })
    );
    
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error marking multiple slots as booked:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Liberar múltiplos slots (cancelamento de agendamento multi-slot)
 * @param {Array} slotIds - Array de IDs dos slots a liberar
 */
export const releaseMultipleSlots = async (slotIds) => {
  try {
    const promises = slotIds.map(slotId => 
      updateDoc(doc(db, COLLECTION, slotId), {
        isBooked: false,
        appointmentId: null,
        updatedAt: new Date().toISOString()
      })
    );
    
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error releasing multiple slots:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Criar múltiplos slots de uma vez (útil para criar agenda semanal)
 */
export const createMultipleSlots = async (slotsArray) => {
  try {
    const promises = slotsArray.map(slot => 
      addDoc(collection(db, COLLECTION), {
        ...slot,
        isBooked: false,
        createdAt: new Date().toISOString()
      })
    );
    
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error creating multiple slots:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listener real-time para slots de uma barbearia
 */
export const subscribeToSlots = (barbershopId, callback) => {
  try {
    console.log('🔄 Iniciando listener de slots para barbearia:', barbershopId);
    
    const q = query(
      collection(db, COLLECTION), 
      where('barbershopId', '==', barbershopId),
      limit(100)
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const slots = [];
      querySnapshot.forEach((doc) => {
        slots.push({ id: doc.id, ...doc.data() });
      });
      
      // Ordenar manualmente por data e time
      slots.sort((a, b) => {
        const dateCompare = a.date.localeCompare(b.date);
        if (dateCompare !== 0) return dateCompare;
        return a.time.localeCompare(b.time);
      });
      console.log('✅ Slots recebidos:', slots.length, 'slots');
      callback(slots);
    }, (error) => {
      console.error('❌ Erro no listener de slots:', error);
      console.error('Detalhes:', error.message);
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('❌ Erro ao criar listener de slots:', error);
    return () => {};
  }
};

/**
 * Notificar usuários na fila de espera quando um slot for liberado
 */
export const notifyWaitlistForSlot = async (slot, barbershop) => {
  try {
    console.log('🔔 Iniciando notificação de waitlist para slot:', { 
      slotId: slot.id, 
      barbershopId: slot.barbershopId, 
      date: slot.date, 
      time: slot.time 
    });
    
    // Buscar waitlist da barbearia para a data específica
    const waitlist = await getWaitlistByBarbershop(slot.barbershopId);
    console.log(`📋 Total de waitlists encontrados: ${waitlist.length}`);
    
    // Filtrar por data e horários preferidos
    const matchingWaitlist = waitlist.filter(item => 
      item.date === slot.date && 
      item.preferredTimes.includes(slot.time)
    );
    
    console.log(`🎯 Waitlists que correspondem (data: ${slot.date}, time: ${slot.time}): ${matchingWaitlist.length}`);
    
    if (matchingWaitlist.length === 0) {
      console.log('❌ Nenhum usuário na fila de espera para este horário');
      return { success: true, notified: 0 };
    }
    
    // Notificar todos os usuários (em ordem de chegada, limitado aos 5 primeiros)
    const toNotify = matchingWaitlist.slice(0, 5);
    console.log(`📤 Preparando para notificar ${toNotify.length} usuários`);
    let notifiedCount = 0;
    
    for (const waitlistItem of toNotify) {
      console.log(`👤 Processando usuário: ${waitlistItem.userId}`);
      const userResult = await getUserById(waitlistItem.userId);
      
      if (userResult.success && userResult.data.email) {
        console.log(`📧 Enviando email para: ${userResult.data.email}`);
        
        // Enviar email
        const emailResult = await sendWaitlistNotificationEmail(
          waitlistItem,
          barbershop,
          slot,
          userResult.data.email
        );
        console.log('✉️ Resultado do envio de email:', emailResult);
        
        // Criar notificação in-app
        console.log('🔔 Criando notificação in-app...');
        const formattedDate = new Date(slot.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const notificationResult = await createNotification({
          userId: waitlistItem.userId,
          type: 'waitlist_available',
          title: '🎉 Horário Disponível!',
          message: `Um horário que você aguardava ficou disponível em ${barbershop.name} para ${formattedDate} às ${slot.time}`,
          data: {
            barbershopId: slot.barbershopId,
            barbershopName: barbershop.name,
            slotId: slot.id,
            date: slot.date,
            time: slot.time
          }
        });
        console.log('📲 Resultado da notificação in-app:', notificationResult);
        
        // Marcar waitlist como notificado
        console.log(`✅ Marcando waitlist ${waitlistItem.id} como notificado`);
        await markAsNotified(waitlistItem.id);
        
        notifiedCount++;
      } else {
        console.log(`⚠️ Usuário sem email ou não encontrado:`, userResult);
      }
    }
    
    console.log(`✅ Total de ${notifiedCount} usuários notificados da fila de espera`);
    return { success: true, notified: notifiedCount };
  } catch (error) {
    console.error('❌ Erro ao notificar fila de espera:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calcular slots necessários baseado na duração do serviço
 * @param {number} serviceDuration - Duração em minutos (30, 60, 90, etc)
 * @param {number} slotInterval - Intervalo de cada slot em minutos (30 ou 60)
 * @returns {number} Quantidade de slots necessários
 */
export const calculateRequiredSlots = (serviceDuration, slotInterval = 30) => {
  return Math.ceil(serviceDuration / slotInterval);
};

/**
 * Verificar e buscar slots consecutivos disponíveis
 * @param {string} barbershopId - ID da barbearia
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} startTime - Horário inicial HH:MM
 * @param {number} slotsNeeded - Quantidade de slots necessários
 * @returns {Object} { success, slots: [], error }
 */
export const findConsecutiveSlots = async (barbershopId, date, startTime, slotsNeeded) => {
  try {
    // Buscar todos os slots do dia
    const result = await getSlotsByBarbershopAndDate(barbershopId, date);
    
    if (!result.success) {
      return { success: false, error: result.error };
    }

    const allSlots = result.data.filter(slot => !slot.isBooked);
    
    // Detectar o intervalo entre slots
    let slotInterval = 30; // Padrão
    if (allSlots.length >= 2) {
      const firstMinutes = parseInt(allSlots[0].time.split(':')[0]) * 60 + parseInt(allSlots[0].time.split(':')[1]);
      const secondMinutes = parseInt(allSlots[1].time.split(':')[0]) * 60 + parseInt(allSlots[1].time.split(':')[1]);
      slotInterval = secondMinutes - firstMinutes;
    }
    
    // Encontrar o índice do slot inicial
    const startIndex = allSlots.findIndex(slot => slot.time === startTime);
    
    if (startIndex === -1) {
      return { 
        success: false, 
        error: 'Horário inicial não encontrado' 
      };
    }

    // Verificar se há slots suficientes disponíveis consecutivamente
    const consecutiveSlots = [];
    
    for (let i = 0; i < slotsNeeded; i++) {
      const slot = allSlots[startIndex + i];
      
      if (!slot) {
        return {
          success: false,
          error: `Horários insuficientes. Serviço requer ${slotsNeeded} slots consecutivos, mas apenas ${i} disponíveis.`
        };
      }
      
      // Verificar se os horários são realmente consecutivos (baseado no intervalo detectado)
      if (i > 0) {
        const prevTime = consecutiveSlots[i - 1].time;
        const currentTime = slot.time;
        
        const prevMinutes = parseInt(prevTime.split(':')[0]) * 60 + parseInt(prevTime.split(':')[1]);
        const currentMinutes = parseInt(currentTime.split(':')[0]) * 60 + parseInt(currentTime.split(':')[1]);
        
        if (currentMinutes - prevMinutes !== slotInterval) {
          return {
            success: false,
            error: `Horários não são consecutivos. Falta disponibilidade entre ${prevTime} e ${currentTime}.`
          };
        }
      }
      
      consecutiveSlots.push(slot);
    }

    return { 
      success: true, 
      slots: consecutiveSlots 
    };
  } catch (error) {
    console.error('Error finding consecutive slots:', error);
    return { success: false, error: error.message };
  }
};
